import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Ban,
  Braces,
  Building2,
  Compass,
  Filter,
  Globe,
  Hash,
  Image,
  Info,
  Map as MapIcon,
  MapPin,
  MonitorSmartphone,
  Ruler,
  Tags,
  TriangleAlert,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'

import { Navigate } from 'react-router'

import { AppShell } from '../../components/AppShell'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCurrentUser } from '../../contexts/UserContext'
import { isSuperAdmin } from '@/data/superAdmins'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  TrafficHistogramApi,
  type HistogramEntry,
  type TrafficType,
} from '@/services/stats/trafficHistogramApi'

/**
 * The dimensions the forecast is broken down by. `objectField` names the field
 * an entry carries its resolved object under, for the types whose values are
 * ids rather than plain text.
 */
const EXPLORER_TYPES: {
  type: TrafficType
  key: string
  icon: LucideIcon
  objectField?: string
}[] = [
  { type: 'SEGMENT', key: 'segments', icon: Users, objectField: 'userSegment' },
  { type: 'CATEGORY', key: 'categories', icon: Tags },
  { type: 'KEYWORD', key: 'keywords', icon: Hash },
  { type: 'KEY_VALUES', key: 'keyValues', icon: Braces },
  { type: 'COUNTRY', key: 'countries', icon: Globe },
  { type: 'REGION', key: 'regions', icon: MapIcon },
  { type: 'CITY', key: 'cities', icon: Building2 },
  { type: 'POSTCODE', key: 'postcodes', icon: MapPin },
  { type: 'DEVICE', key: 'devices', icon: MonitorSmartphone },
  { type: 'CREATIVE_SIZE', key: 'creativeSizes', icon: Ruler },
  { type: 'CREATIVE_TYPE', key: 'creativeTypes', icon: Image },
]

/** The API returns at most this many entries per dimension. */
const MAX_ENTRIES = 50

type Filters = Partial<Record<TrafficType, string>>

/** One day as `yyyy-mm-dd`, which is what a date input reads and writes. */
function isoDay(offsetDays: number): string {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date.toISOString().slice(0, 10)
}

/** The comma-separated filter boxes as the API's `filter` map. */
function toFilterMap(filters: Filters): Partial<Record<TrafficType, string[]>> {
  const map: Partial<Record<TrafficType, string[]>> = {}
  for (const [type, raw] of Object.entries(filters)) {
    const values = (raw ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
    if (values.length > 0) map[type as TrafficType] = values
  }
  return map
}

/**
 * Explore: what the network's traffic looks like over a forecast period, broken
 * down by every dimension the ad server samples, and narrowable by any value
 * found. Network-wide — the per-object version lives on the ad unit and site.
 */
export function ExplorePage() {
  const { t } = useTranslation()
  const currentUser = useCurrentUser()
  const [startDate, setStartDate] = useState(() => isoDay(0))
  const [endDate, setEndDate] = useState(() => isoDay(7))
  const [filters, setFilters] = useState<Filters>({})
  const [showAllFilters, setShowAllFilters] = useState(false)
  // Held apart from the inputs so editing a box does not re-run the forecast.
  const [request, setRequest] = useState<{
    startDate: string
    endDate: string
    filters: Filters
  } | null>(null)

  const hasFilter = Object.values(filters).some((value) => (value ?? '').trim().length > 0)

  const { data, isFetching, error } = useQuery({
    queryKey: ['explore', request],
    queryFn: () =>
      TrafficHistogramApi.get({
        types: EXPLORER_TYPES.map((entry) => entry.type),
        filter: toFilterMap(request!.filters),
        startDate: new Date(request!.startDate).toISOString(),
        endDate: new Date(request!.endDate).toISOString(),
      }),
    enabled: !!request,
    staleTime: 5 * 60 * 1000,
  })

  /** Adds a value found in the results to its dimension's filter box. */
  const addToFilter = (type: TrafficType, value: string) => {
    setFilters((previous) => {
      const current = (previous[type] ?? '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
      if (current.includes(value)) return previous
      return { ...previous, [type]: [...current, value].join(', ') }
    })
  }

  const removeFromFilter = (type: TrafficType, value: string) => {
    setFilters((previous) => ({
      ...previous,
      [type]: (previous[type] ?? '')
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry && entry !== value)
        .join(', '),
    }))
  }

  /** The filter as it stands, for the summary shown while the panel is closed. */
  const activeFilters = EXPLORER_TYPES.map((entry) => ({
    ...entry,
    values: (filters[entry.type] ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean),
  })).filter((entry) => entry.values.length > 0)

  const results = useMemo(
    () =>
      EXPLORER_TYPES.map((entry) => ({
        ...entry,
        rows: (data?.[entry.type]?.histogram ?? []) as HistogramEntry[],
      })),
    [data]
  )

  const firstWithRows = results.find((entry) => entry.rows.length > 0) ?? results[0]

  /**
   * The largest expected request count anywhere in the response. With a filter
   * applied, the value matching it most closely carries the whole of the
   * filtered traffic, so this is the ceiling on what the filter can deliver.
   */
  const maxExpectedRequests = results.reduce(
    (max, entry) =>
      entry.rows.reduce((rowMax, row) => Math.max(rowMax, row.expectedNextWeek), max),
    0
  )

  /** Dimensions the filter narrows, so their tabs can say so. */
  const filteredTypes = new Set(activeFilters.map((entry) => entry.type))

  /** The filtered values per dimension, so their rows can say so too. */
  const filteredValues = new Map(
    activeFilters.map((entry) => [entry.type, new Set(entry.values)])
  )

  /** Shown once: inside the summary card when there is one, otherwise on its own. */
  const summarised = !showAllFilters && activeFilters.length > 0
  const filterButtons = (
    <div className="flex items-center gap-2">
      <CollapsibleTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          <Filter className="size-3.5" />
          {showAllFilters ? t('explore.hideFilters') : t('explore.showFilters')}
        </Button>
      </CollapsibleTrigger>
      {hasFilter && (
        <Button type="button" variant="ghost" size="sm" onClick={() => setFilters({})}>
          <Ban className="size-3.5" />
          {t('explore.clearFilters')}
        </Button>
      )}
    </div>
  )

  if (!isSuperAdmin(currentUser?.username)) {
    return <Navigate to="/advertising" replace />
  }

  return (
    <AppShell
      crumbs={[
        { label: t('nav.adServing'), href: '/advertising' },
        { label: t('explore.title') },
      ]}
    >
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('explore.title')}</h1>
            <p>{t('explore.description')}</p>
          </div>
        </div>

        <Card tone="insight">
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Compass} />
              {t('explore.forecast')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:max-w-lg">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="explore-start">{t('explore.startDate')}</Label>
                <Input
                  id="explore-start"
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="explore-end">{t('explore.endDate')}</Label>
                <Input
                  id="explore-end"
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>

            <Collapsible open={showAllFilters} onOpenChange={setShowAllFilters}>
              {summarised && (
                <Card tone="delivery" className="bg-muted/40 lg:w-1/2">
                  <CardHeader>
                    <CardTitle className="text-sm">
                      <CardIcon icon={Filter} />
                      {t('explore.selectedFilters')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    {activeFilters.map((entry) => (
                      <div key={entry.type} className="flex flex-wrap items-center gap-1.5">
                        <span className="w-28 shrink-0 text-xs text-muted-foreground">
                          {t(`explore.types.${entry.key}`)}
                        </span>
                        {entry.values.map((value) => (
                          <Badge key={value} variant="secondary" className="gap-1 pr-1">
                            {value}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-4"
                              title={t('explore.removeFromFilter')}
                              onClick={() => removeFromFilter(entry.type, value)}
                            >
                              <X className="size-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    ))}
                    <div className="pt-1">{filterButtons}</div>
                  </CardContent>
                </Card>
              )}

              {!summarised && filterButtons}

              <CollapsibleContent className="pt-3">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {EXPLORER_TYPES.map((entry) => (
                    <div key={entry.type} className="flex flex-col gap-1.5">
                      <Label htmlFor={`explore-filter-${entry.type}`}>
                        {t(`explore.types.${entry.key}`)}
                      </Label>
                      <Input
                        id={`explore-filter-${entry.type}`}
                        value={filters[entry.type] ?? ''}
                        placeholder={t('explore.filterPlaceholder')}
                        onChange={(event) =>
                          setFilters((previous) => ({
                            ...previous,
                            [entry.type]: event.target.value,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div>
              <Button
                type="button"
                loading={isFetching}
                onClick={() => setRequest({ startDate, endDate, filters })}
              >
                {t('explore.explore')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Alert variant="info">
          <Info className="size-4" />
          <AlertDescription>{t('explore.approximate')}</AlertDescription>
        </Alert>

        {request && (
          <Card>
            <CardHeader>
              <CardTitle>{t('explore.resultsTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {error && (
                <Alert variant="destructive">
                  <TriangleAlert className="size-4" />
                  <AlertDescription>{apiErrorDescription(error)}</AlertDescription>
                </Alert>
              )}

              {isFetching && <Skeleton className="h-64 w-full" />}

              {!isFetching && !error && data && (
                <>
                  <p className="text-sm text-muted-foreground">
                    {Object.keys(toFilterMap(request.filters)).length > 0
                      ? t('explore.resultsFiltered')
                      : t('explore.resultsAll')}
                  </p>

                  {Object.keys(toFilterMap(request.filters)).length > 0 &&
                    maxExpectedRequests > 0 && (
                      <p className="text-sm">
                        {t('explore.maxExpectedRequests')}{' '}
                        <span className="font-semibold tabular-nums">
                          {maxExpectedRequests.toLocaleString()}
                        </span>
                      </p>
                    )}

                  <Separator />

                  <Tabs
                    defaultValue={firstWithRows.key}
                    orientation="vertical"
                    className="gap-4"
                  >
                    <TabsList className="w-56 shrink-0 gap-0.5">
                      {results.map((entry) => (
                        <TabsTrigger
                          key={entry.key}
                          value={entry.key}
                          className="h-9 gap-2 px-3"
                        >
                          <entry.icon />
                          <span className="flex-1 text-left">
                            {t(`explore.types.${entry.key}`)}
                          </span>
                          {filteredTypes.has(entry.type) && (
                            <Filter
                              className="size-3 text-(--ad-link)"
                              aria-label={t('explore.tabFiltered')}
                            />
                          )}
                          <Badge
                            variant={filteredTypes.has(entry.type) ? 'default' : 'secondary'}
                          >
                            {entry.rows.length >= MAX_ENTRIES
                              ? `${MAX_ENTRIES}+`
                              : entry.rows.length}
                          </Badge>
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {results.map((entry) => (
                      <TabsContent
                        key={entry.key}
                        value={entry.key}
                        className="flex min-w-0 flex-1 flex-col gap-3"
                      >
                        <p className="text-sm text-muted-foreground">
                          {entry.rows.length >= MAX_ENTRIES
                            ? t('explore.foundTop', {
                                count: MAX_ENTRIES,
                                type: t(`explore.types.${entry.key}`),
                              })
                            : t('explore.found', {
                                count: entry.rows.length,
                                type: t(`explore.types.${entry.key}`),
                              })}
                        </p>

                        {entry.rows.length > 0 && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>{t(`explore.types.${entry.key}`)}</TableHead>
                                <TableHead className="text-right">
                                  {t('explore.presence')}
                                </TableHead>
                                <TableHead className="text-right">
                                  {t('explore.expectedRequests')}
                                </TableHead>
                                <TableHead />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {entry.rows.map((row) => {
                                // A value that names an object shows the object's
                                // name, and filters by its id.
                                const object = entry.objectField
                                  ? (row[entry.objectField] as
                                      | { id: string; name: string }
                                      | undefined)
                                  : undefined
                                const filterValue = object?.id ?? row.value
                                const isFiltered = !!filteredValues
                                  .get(entry.type)
                                  ?.has(filterValue)
                                return (
                                  <TableRow
                                    key={`${entry.key}-${filterValue}`}
                                    // This value is one of the ones narrowing
                                    // the traffic being analysed.
                                    className={isFiltered ? 'bg-primary/10' : undefined}
                                    title={isFiltered ? t('explore.rowFiltered') : undefined}
                                  >
                                    <TableCell>
                                      <span className="block">{object?.name ?? row.value}</span>
                                      {object && (
                                        <span className="block text-xs text-muted-foreground">
                                          {object.id}
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {row.frequency.toFixed(0)}%
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {row.expectedNextWeek.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        title={
                                          isFiltered
                                            ? t('explore.removeFromFilter')
                                            : t('explore.addToFilter')
                                        }
                                        onClick={() =>
                                          isFiltered
                                            ? removeFromFilter(entry.type, filterValue)
                                            : addToFilter(entry.type, filterValue)
                                        }
                                      >
                                        {isFiltered ? (
                                          <X className="size-3.5" />
                                        ) : (
                                          <Filter className="size-3.5" />
                                        )}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  )
}
